import React, { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import './App.css';

const socket = io(`https://coasterweb-backend.onrender.com/`);

function App() {
  const [userId, setUserId] = useState(null);
  const [usernameSet, setUsernameSet] = useState(false);
  const [inputUsername, setInputUsername] = useState('');
  
  const [roomCode, setRoomCode] = useState('');
  const [inputRoomCode, setInputRoomCode] = useState('');
  const [roomUsers, setRoomUsers] = useState([]);
  const [inRoom, setInRoom] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [roomLimit, setRoomLimit] = useState(3);
  const [startingHealth, setStartingHealth] = useState(2);
  const [roundTime, setRoundTime] = useState(15);
  const [currentCount, setCurrentCount] = useState(0);
  const [isHost, setIsHost] = useState(false);
  const [gameState, setGameState] = useState('main');
  const [myHP, setMyHP] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [useImperial, setUseImperial] = useState(true);
  const [isPublicRoom, setIsPublicRoom] = useState(false);
  const errorTimeoutRef = useRef(null);
  
  const [roundNumber, setRoundNumber] = useState(0);
  const [coasters, setCoasters] = useState([]);
  const [criterion, setCriterion] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [roundResults, setRoundResults] = useState(null);
  const [difficultyMode, setDifficultyMode] = useState('medium');
  const [selectedEmote, setSelectedEmote] = useState(null);

  // Sound effects
  const soundRefs = useRef({
    guess: new Audio('https://assets.mixkit.co/active_storage/sfx/2568/2568-preview.mp3'), // Click sound
    roundStart: new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3'), // Game start
    roundEnd: new Audio('https://assets.mixkit.co/active_storage/sfx/2018/2018-preview.mp3') // Success sound
  });

  useEffect(() => {
    const chatMessagesEl = document.querySelector('.chat-messages');
    if (chatMessagesEl) {
      chatMessagesEl.scrollTop = chatMessagesEl.scrollHeight;
    }
  }, [chatMessages]);

  useEffect(() => {
    socket.on('connect', () => {
      console.log('Connected to server');
    });

    socket.on('userIdAssigned', (id) => {
      setUserId(id);
      console.log(`Assigned user ID: ${id}`);
    });

    socket.on('usernameSet', (name) => {
      setUsernameSet(true);
      addChatMessage('System', `Username set to: ${name}`);
    });

    socket.on('roomCreated', ({ roomCode, isHost }) => {
      setRoomCode(roomCode);
      setIsHost(isHost);
      setInRoom(true);
      setGameState('lobby');
      setChatMessages([]);
      setIsPublicRoom(false);
      addChatMessage('System', `You created room ${roomCode}!`);
    });

    socket.on('joinedRoom', ({ roomCode, gameState, isPublic }) => {
      setRoomCode(roomCode);
      setInRoom(true);
      setIsHost(false);
      setGameState(gameState);
      setChatMessages([]);
      setIsPublicRoom(isPublic || false);
      addChatMessage('System', `You joined room ${roomCode}!`);
    });

    socket.on('roomAlreadyJoined', (code) => {
      addChatMessage('System', `You already joined room: ${code}`);
    });

    socket.on('roomUsersUpdate', ({ users, roomLimit, currentCount, gameState, startingHealth, roundTime, useImperial, isPublic }) => {
      setRoomUsers(users);
      setCurrentCount(currentCount);
      setRoomLimit(roomLimit);
      setGameState(gameState);
      if (startingHealth) setStartingHealth(startingHealth);
      if (roundTime) setRoundTime(roundTime);
      if (useImperial !== undefined) setUseImperial(useImperial);
      if (isPublic !== undefined) setIsPublicRoom(isPublic);
      
      const currentUser = users.find(u => u.userId === userId);
      if (currentUser) {
        setIsHost(currentUser.isHost);
        setMyHP(currentUser.hp);
      }
    });

    socket.on('newHostAssigned', (newHostId) => {
      setIsHost(userId === newHostId);
    });

    socket.on('chatMessage', ({ userId, username, message }) => {
      addChatMessage(username, message);
    });

    socket.on('error', (err) => {
      addChatMessage('Error', err);
      showError(err);
    });

    socket.on('gameStarted', ({ message }) => {
      setGameState('playing');
      addChatMessage('System', message);
      setRoundResults(null);
    });

    socket.on('newRound', ({ roundNumber, coasters, criterion, timeLimit }) => {
      setRoundNumber(roundNumber);
      setCoasters(coasters);
      setCriterion(criterion);
      setTimeRemaining(timeLimit / 1000);
      setHasAnswered(false);
      setSelectedAnswer(null);
      setRoundResults(null);
      
      // Play round start sound
      soundRefs.current.roundStart.play().catch(e => console.log('Audio play failed:', e));
    });

    socket.on('playerAnswered', ({ userId: answeredUserId, username }) => {
      // Play guess sound
      soundRefs.current.guess.play().catch(e => console.log('Audio play failed:', e));
      
      if (answeredUserId !== userId) {
        addChatMessage('System', `${username} has answered!`);
      }
    });

    socket.on('roundEnded', ({ correctCoasterId, correctCoaster, allCoasters, results, criterion }) => {
      setRoundResults({ correctCoasterId, correctCoaster, allCoasters, results, criterion });
      setHasAnswered(false);
      setSelectedAnswer(null);
      
      // Play round end sound
      soundRefs.current.roundEnd.play().catch(e => console.log('Audio play failed:', e));
    });

    socket.on('gameEnded', ({ message, winner }) => {
      setGameState('lobby');
      setRoundNumber(0);
      setCoasters([]);
      setCriterion(null);
      setRoundResults(null);
      setMyHP(0);
      setTimeRemaining(0);
      setHasAnswered(false);
      setSelectedAnswer(null);
      addChatMessage('System', message);
    });

    socket.on('leftRoom', () => {
      setInRoom(false);
      setRoomCode('');
      setRoomUsers([]);
      setChatMessages([]);
      setGameState('main');
      setIsHost(false);
      setRoundNumber(0);
      setCoasters([]);
      setCriterion(null);
      setRoundResults(null);
      setIsPublicRoom(false);
    });

    return () => {
      socket.off('connect');
      socket.off('disconnect');
      socket.off('userIdAssigned');
      socket.off('usernameSet');
      socket.off('roomCreated');
      socket.off('joinedRoom');
      socket.off('roomAlreadyJoined');
      socket.off('roomUsersUpdate');
      socket.off('newHostAssigned');
      socket.off('chatMessage');
      socket.off('error');
      socket.off('gameStarted');
      socket.off('newRound');
      socket.off('playerAnswered');
      socket.off('roundEnded');
      socket.off('gameEnded');
      socket.off('leftRoom');
    };
  }, [userId]);

  const addChatMessage = (username, message) => {
    setChatMessages(prev => [...prev, { username, message }]);
  };

  useEffect(() => {
    if (gameState === 'playing' && timeRemaining > 0 && !roundResults) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => Math.max(0, prev - 1));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [gameState, timeRemaining, roundResults]);

  const handleSetUsername = () => {
    if (!inputUsername.trim()) {
      showError('Username cannot be empty');
      return;
    }
    socket.emit('setUsername', inputUsername);
    setInputUsername('');
  };

  const showError = (message) => {
    if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
    setErrorMessage(message);
    errorTimeoutRef.current = setTimeout(() => setErrorMessage(''), 3000);
  };

  const quickQueue = () => {
    if (!inputUsername.trim()) {
      showError('Please enter a username');
      return;
    }
    if (!usernameSet) {
      socket.emit('setUsername', inputUsername);
    }
    setTimeout(() => {
      socket.emit('quickQueue');
    }, 100);
  };

  const createRoom = () => {
    if (!inputUsername.trim()) {
      showError('Please enter a username');
      return;
    }
    if (!usernameSet) {
      socket.emit('setUsername', inputUsername);
    }
    setTimeout(() => {
      socket.emit('createRoom', { maxPlayers: 3, startingHealth: 2, roundTime: 15000, useImperial: true });
    }, 100);
  };

  const joinRoom = () => {
    if (!inputUsername.trim()) {
      showError('Please enter a username');
      return;
    }
    if (!inputRoomCode.trim()) {
      showError('Please enter a room code');
      return;
    }
    if (!usernameSet) {
      socket.emit('setUsername', inputUsername);
    }
    setTimeout(() => {
      socket.emit('joinRoom', inputRoomCode.toUpperCase().trim());
      setInputRoomCode('');
    }, 100);
  };

  const leaveRoom = () => {
    socket.emit('leaveRoom');
  };

  const sendMessage = () => {
    if (newMessage.trim()) {
      socket.emit('chatMessage', newMessage);
      setNewMessage('');
    }
  };

  const sendEmote = (emote) => {
    socket.emit('chatMessage', emote);
    setSelectedEmote(null);
  };

  const updateGameSettings = (setting, value) => {
    if (setting === 'maxPlayers') {
      const newLimit = Math.max(currentCount, value);
      setRoomLimit(newLimit);
      socket.emit('updateGameSettings', { maxPlayers: newLimit });
    } else if (setting === 'startingHealth') {
      setStartingHealth(value);
      socket.emit('updateGameSettings', { startingHealth: value });
    } else if (setting === 'roundTime') {
      setRoundTime(value);
      socket.emit('updateGameSettings', { roundTime: value * 1000 });
    } else if (setting === 'useImperial') {
      setUseImperial(value);
      socket.emit('updateGameSettings', { useImperial: value });
    } else if (setting === 'difficultyMode') {
      setDifficultyMode(value);
      socket.emit('updateGameSettings', { difficultyMode: value });
    }
  };

  const startGame = () => {
    if (isHost) {
      socket.emit('startGame');
    }
  };

  const endGame = () => {
    if (isHost) {
      socket.emit('endGame');
    }
  };

  const selectCoaster = (coasterId) => {
    if (hasAnswered || roundResults) return;
    
    setSelectedAnswer(coasterId);
    setHasAnswered(true);
    socket.emit('submitAnswer', coasterId);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleUsernameKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSetUsername();
    }
  };

  const handleJoinKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      joinRoom();
    }
  };

  const formatStatValue = (stat, key) => {
    if (key === 'height' && useImperial) return `${(stat * 3.28084).toFixed(0)}ft`;
    if (key === 'height') return `${stat}m`;
    if (key === 'speed' && useImperial) return `${(stat * 0.621371).toFixed(0)} mph`;
    if (key === 'speed') return `${stat} km/h`;
    if (key === 'length' && useImperial) return `${(stat * 3.28084).toFixed(0)}ft`;
    if (key === 'length') return `${stat}m`;
    if (key === 'inversions') return `${stat}`;
    if (key === 'year') return `${stat}`;
    return stat;
  };

  if (gameState === 'main' && !inRoom) {
    return (
      <div className="App">
        <div className="header-banner">
          <h1>Untitled Coaster Game</h1>
          <img src="https://i.imgur.com/FdRVeZw.gif" alt="" width="180" height="100"/>
        </div>
        <div className="main-page-container">
          <div className="main-page-card">
            <input
              type="text"
              placeholder="Enter your username"
              value={inputUsername}
              onChange={(e) => setInputUsername(e.target.value)}
              onKeyPress={handleUsernameKeyPress}
              maxLength={20}
            />
            
            {errorMessage && (
              <div style={{ color: '#ff6b6b', fontSize: '0.9rem', marginTop: '10px', fontWeight: 'bold' }}>
                {errorMessage}
              </div>
            )}
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '20px' }}>
              <button 
                onClick={quickQueue} 
                style={{ 
                  padding: '15px 20px', 
                  fontSize: '18px', 
                  backgroundColor: '#4caf50', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '5px', 
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Quick Queue (Public Lobby)
              </button>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ flex: 1, height: '1px', background: '#ddd' }}></div>
                <span style={{ color: '#999', fontSize: '14px' }}>or</span>
                <div style={{ flex: 1, height: '1px', background: '#ddd' }}></div>
              </div>
              
              <button onClick={createRoom} style={{ padding: '12px 20px', fontSize: '16px', backgroundColor: '#667eea', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>
                Create Private Game
              </button>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ flex: 1, height: '1px', background: '#ddd' }}></div>
                <span style={{ color: '#999', fontSize: '14px' }}>or</span>
                <div style={{ flex: 1, height: '1px', background: '#ddd' }}></div>
              </div>
              
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input
                  type="text"
                  placeholder="Room code"
                  value={inputRoomCode}
                  onChange={(e) => setInputRoomCode(e.target.value.toUpperCase())}
                  onKeyPress={handleJoinKeyPress}
                  style={{ flex: 1, padding: '10px 12px', fontSize: '16px', border: '1px solid #ddd', borderRadius: '5px', height: 'auto' }}
                />
                <button onClick={joinRoom} style={{ padding: '10px 20px', fontSize: '16px', backgroundColor: '#667eea', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>
                  Join Game
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <div className="header-banner">
        <h1>Untitled Coaster Game</h1>
        <img src="https://raw.githubusercontent.com/Watkin81/watkin81.github.io/refs/heads/main/images/header/Manta_SWSD.jpg" alt="" width="180" height="100"/>
      </div>

      <div className="main-container">
        <div className="left-sidebar">
          <div className="players-container">
            <div className="players-header">Players</div>
            <div className="lobby-info">
              <p><strong>Room:</strong> {roomCode} {isPublicRoom && <span style={{ color: '#4caf50', fontSize: '0.85em' }}>🌐 Public</span>}</p>
              <p><strong>Count:</strong> {currentCount} / {roomLimit}</p>
            </div>
            <div className="players-list">
              {roomUsers.map(user => (
                <div key={user.userId} className="player-item">
                  <span className="player-name">
                    {user.isHost ? '👑 ' : ''}{user.username}{user.userId === userId ? ' (You)' : ''}
                  </span>
                  <span className="player-hp">❤️ {user.hp}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="center-section">
          {gameState === 'lobby' && (
            <div>
              <div className="settings-panel">
                <h3>Game Settings</h3>
                
                {isPublicRoom && (
                  <div style={{ 
                    background: 'rgba(76, 175, 80, 0.1)', 
                    padding: '10px', 
                    borderRadius: '5px', 
                    marginBottom: '15px',
                    border: '1px solid rgba(76, 175, 80, 0.3)'
                  }}>
                    <p style={{ margin: 0, color: '#4caf50', fontWeight: 'bold', fontSize: '0.9rem' }}>
                      🌐 Public Lobby
                    </p>
                  </div>
                )}
                
                {!isPublicRoom && (
                  <div style={{ 
                    background: 'rgba(102, 126, 234, 0.1)', 
                    padding: '10px', 
                    borderRadius: '5px', 
                    marginBottom: '15px',
                    border: '1px solid rgba(102, 126, 234, 0.3)'
                  }}>
                    <p style={{ margin: 0, color: '#667eea', fontWeight: 'bold', fontSize: '0.9rem' }}>
                      🔒 Private Lobby - Invite others with the room code: {roomCode}
                    </p>
                  </div>
                )}
                
                <div className="setting-row">
                  <label>Max Players:</label>
                  <select 
                    value={roomLimit} 
                    onChange={(e) => updateGameSettings('maxPlayers', Number(e.target.value))}
                    disabled={!isHost || (isPublicRoom && currentCount > 1)}
                  >
                    {[2, 3, 4, 5, 6, 7, 8].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>

                <div className="setting-row">
                  <label>Starting Health:</label>
                  <select 
                    value={startingHealth} 
                    onChange={(e) => updateGameSettings('startingHealth', Number(e.target.value))}
                    disabled={!isHost}
                  >
                    {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>

                <div className="setting-row">
                  <label>Round Time (seconds):</label>
                  <select 
                    value={roundTime} 
                    onChange={(e) => updateGameSettings('roundTime', Number(e.target.value))}
                    disabled={!isHost}
                  >
                    {[10, 15, 20, 30].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>

                <div className="setting-row">
                  <label>Units:</label>
                  <select 
                    value={useImperial ? 'imperial' : 'metric'} 
                    onChange={(e) => updateGameSettings('useImperial', e.target.value === 'imperial')}
                    disabled={!isHost}
                  >
                    <option value="imperial">Imperial (ft, mph)</option>
                    <option value="metric">Metric (m, km/h)</option>
                  </select>
                </div>

                <div className="setting-row">
                  <label>Difficulty:</label>
                  <select 
                    value={difficultyMode} 
                    onChange={(e) => updateGameSettings('difficultyMode', e.target.value)}
                    disabled={!isHost}
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>

                {!isHost && (
                  <p className="host-only-note">* Host only settings</p>
                )}
                {isPublicRoom && isHost && currentCount > 1 && (
                  <p className="host-only-note">* Max Players locked once other players join</p>
                )}
              </div>

              <div className="game-controls">
                <button onClick={leaveRoom}>Leave Room</button>
                <button 
                  onClick={startGame} 
                  disabled={!isHost || currentCount < 2}
                >
                  {!isHost ? 'Waiting for Host...' : currentCount < 2 ? 'Waiting for Players...' : 'Start Game'}
                </button>
              </div>
            </div>
          )}

          {gameState === 'playing' && (
            <div>
              <h2>Round {roundNumber}</h2>
              
              {criterion && !roundResults && (
                <>
                  <div className="criterion-display">
                    <h3>Pick the roller coaster with:</h3>
                    <h2>{criterion.type}: {criterion.displayValue}</h2>
                  </div>
                  
                  <div className="timer">
                    Time Remaining: {timeRemaining}s
                  </div>

                  {myHP > 0 ? (
                    <div className="coaster-options">
                      {coasters.map(coaster => (
                        <div 
                          key={coaster.id} 
                          className={`coaster-card ${selectedAnswer === coaster.id ? 'selected' : ''} ${hasAnswered ? 'disabled' : ''}`}
                          onClick={() => selectCoaster(coaster.id)}
                        >
                          {coaster.mainPicture && (
                            <img 
                              src={coaster.mainPicture} 
                              alt={coaster.name}
                              className="coaster-image"
                            />
                          )}
                          <div className="coaster-info">
                            <h3>{coaster.name}</h3>
                            <p>{coaster.park}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="eliminated">
                      <h3>You have been eliminated!</h3>
                      <p>You are now spectating!</p>
                    </div>
                  )}
                </>
              )}

              {roundResults && (
                <div className="round-results">
                  <h3>Round Results</h3>
                  
                  <div className="all-coasters-stats">
                    <h4>Coaster Stats:</h4>
                    <div className="stats-grid-compact">
                      {roundResults.allCoasters.map(coaster => {
                        const criterionKey = roundResults.criterion?.key;
                        
                        const getStatLabel = (key) => {
                          if (key === 'height') return 'Height';
                          if (key === 'speed') return 'Speed';
                          if (key === 'inversions') return 'Inversions';
                          if (key === 'length') return 'Length';
                          if (key === 'year') return 'Year';
                          if (key === 'park') return 'Park';
                          return key;
                        };
                        
                        const getStatValue = (coaster, key) => {
                          if (key === 'height' && coaster.stats?.height) {
                            return formatStatValue(coaster.stats.height, 'height');
                          }
                          if (key === 'speed' && coaster.stats?.speed) {
                            return formatStatValue(coaster.stats.speed, 'speed');
                          }
                          if (key === 'inversions' && coaster.stats?.inversions !== undefined && coaster.stats.inversions !== null) {
                            return coaster.stats.inversions;
                          }
                          if (key === 'length' && coaster.stats?.length) {
                            return formatStatValue(coaster.stats.length, 'length');
                          }
                          if (key === 'year' && coaster.stats?.year) {
                            return coaster.stats.year;
                          }
                          if (key === 'park') {
                            return coaster.park;
                          }
                          return null;
                        };
                        
                        const statLabel = getStatLabel(criterionKey);
                        const statValue = getStatValue(coaster, criterionKey);
                        
                        return (
                          <div key={coaster.id} className={`stat-card-compact ${coaster.id === roundResults.correctCoasterId ? 'correct' : ''}`}>
                            {coaster.mainPicture && (
                              <img 
                                src={coaster.mainPicture} 
                                alt={coaster.name}
                                className="stat-coaster-image-compact"
                              />
                            )}
                            <h4>{coaster.name}</h4>
                            <p className="park-compact">{coaster.park}</p>
                            {statValue !== null && (
                              <p className="stat-value"><strong>{statLabel}:</strong> {statValue}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  <div className="player-results">
                    <h4>Player Results:</h4>
                    {roundResults.results.map(result => (
                      <div key={result.userId} className={`player-result ${result.isCorrect ? 'correct' : 'incorrect'}`}>
                        <span className="player-result-name">{result.username}</span>
                        <span className="player-result-status">
                          {result.tooSlow ? (
                            <span>⏱️ Too Slow</span>
                          ) : result.isCorrect ? (
                            <span>✅ Correct {result.streak >= 3 && <span>🔥x{result.streak}</span>}</span>
                          ) : result.answeredCoaster ? (
                            <span>❌ Guessed: {result.answeredCoaster.name}</span>
                          ) : (
                            <span>❌ No Answer</span>
                          )}
                        </span>
                        <span className="player-result-hp">❤️ {result.hp}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {isHost && gameState === 'playing' && (
                <button onClick={endGame}>End Game</button>
              )}
            </div>
          )}
        </div>

        <div className="right-sidebar">
          <div className="chat-container">
            <div className="chat-header">Chat</div>
            <div className="chat-messages">
              {chatMessages.map((msg, index) => (
                <div 
                  key={index} 
                  className={`chat-message ${msg.username === 'System' ? 'system-message' : msg.username === 'Error' ? 'error-message' : ''}`}
                >
                  <strong>{msg.username}:</strong> {msg.message}
                </div>
              ))}
            </div>
            <div className="chat-input-area">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Enter yo text..."
                onKeyPress={handleKeyPress}
              />
              <div className="emote-buttons">
                <button 
                  className="emote-trigger"
                  onClick={() => setSelectedEmote(selectedEmote ? null : true)}
                  title="Send an emote"
                >
                  😂
                </button>
                {selectedEmote && (
                  <div className="emote-picker">
                    <button className="emote" onClick={() => sendEmote('😂')}>😂</button>
                    <button className="emote" onClick={() => sendEmote('🔥')}>🔥</button>
                    <button className="emote" onClick={() => sendEmote('💀')}>💀</button>
                    <button className="emote" onClick={() => sendEmote('🤔')}>🤔</button>
                    <button className="emote" onClick={() => sendEmote('👏')}>👏</button>
                    <button className="emote" onClick={() => sendEmote('😤')}>😤</button>
                  </div>
                )}
              </div>
              <button onClick={sendMessage}>Send</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;